import React, { useState } from "react";
import { motion } from "framer-motion";
import { ArrowLeft, TrendingUp, Info } from "lucide-react";
import { MARKET_PROFESSIONS } from "./marketData";

const LEVELS = {
  junior: { label: "Junior", accent: "#B2911E" },
  pleno:  { label: "Pleno",  accent: "#008572" },
  senior: { label: "Senior", accent: "#003326" },
};

function formatRange(raw) {
  // "R$2–3.5k" → "R$ 2.000 – R$ 3.500"
  // "R$12–20k" → "R$ 12.000 – R$ 20.000"
  const m = raw.match(/R\$\s*([\d.]+)\s*[–-]\s*([\d.]+)\s*k/i);
  if (!m) return raw;
  const fmt = (n) => "R$ " + (parseFloat(n) * 1000).toLocaleString("pt-BR", { maximumFractionDigits: 0 });
  return `${fmt(m[1])} – ${fmt(m[2])}`;
}

export default function IncomeSimulator({ initialProfession, onBack }) {
  const [selectedId, setSelectedId] = useState(initialProfession?.id || MARKET_PROFESSIONS[0].id);

  const profession = MARKET_PROFESSIONS.find(p => p.id === selectedId) || MARKET_PROFESSIONS[0];
  const scenarios = profession.incomeData.scenarios;
  const models = profession.incomeData.models;

  return (
    <motion.div
      initial={{ opacity: 0, x: 30 }}
      animate={{ opacity: 1, x: 0 }}
      className="space-y-5"
    >
      <button onClick={onBack} className="flex items-center gap-2 text-sm font-medium" style={{ color: "#646C6F" }}>
        <ArrowLeft className="w-4 h-4" /> Voltar
      </button>

      {/* Header */}
      <div>
        <div className="flex items-center gap-2 mb-1">
          <TrendingUp className="w-4 h-4" style={{ color: "#00DCA4" }} />
          <h2 className="text-lg font-bold" style={{ fontFamily: "Poppins, sans-serif", color: "#003326" }}>
            Remuneração por senioridade
          </h2>
        </div>
        <p className="text-sm" style={{ color: "#99A6AB" }}>
          Estimativas baseadas em dados de mercado.
        </p>
      </div>

      {/* Profession Selector */}
      <div className="bg-white rounded-2xl p-5" style={{ border: "1px solid #E5E9EA" }}>
        <label className="text-xs font-semibold uppercase tracking-wider block mb-2" style={{ color: "#99A6AB" }}>
          Profissão
        </label>
        <select
          value={selectedId}
          onChange={e => setSelectedId(e.target.value)}
          className="w-full text-sm font-semibold px-4 py-3 focus:outline-none appearance-none cursor-pointer"
          style={{
            color: "#003326",
            background: "#F9FAFA",
            borderRadius: 12,
            border: "1px solid #E5E9EA",
          }}
        >
          {MARKET_PROFESSIONS.map(p => (
            <option key={p.id} value={p.id}>{p.name}</option>
          ))}
        </select>

        {models && models.length > 0 && (
          <div className="flex items-center gap-2 mt-3">
            <span className="text-xs" style={{ color: "#99A6AB" }}>Modelos comuns:</span>
            {models.map(m => (
              <span key={m} className="text-xs font-medium px-2 py-0.5 rounded-full" style={{ background: "#F2F4F4", color: "#646C6F" }}>
                {m}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Salary table */}
      <div className="bg-white rounded-2xl overflow-hidden" style={{ border: "1px solid #E5E9EA" }}>
        {/* Table header */}
        <div className="grid grid-cols-2 px-5 py-3" style={{ background: "#F9FAFA", borderBottom: "1px solid #E5E9EA" }}>
          <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: "#99A6AB" }}>Nível</span>
          <span className="text-xs font-semibold uppercase tracking-wider text-right" style={{ color: "#99A6AB" }}>Faixa mensal</span>
        </div>

        {/* Rows */}
        {Object.entries(scenarios).map(([key, value], i, arr) => {
          const level = LEVELS[key];
          return (
            <motion.div
              key={key}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: i * 0.05 }}
              className="grid grid-cols-2 items-center px-5 py-4"
              style={i < arr.length - 1 ? { borderBottom: "1px solid #F2F4F4" } : {}}
            >
              <div className="flex items-center gap-3">
                <div
                  className="w-1 h-8 rounded-full"
                  style={{ background: level.accent }}
                />
                <span className="text-sm font-semibold" style={{ color: "#2E3233" }}>
                  {level.label}
                </span>
              </div>
              <p className="text-right text-base font-bold tabular-nums" style={{ fontFamily: "Poppins, sans-serif", color: "#003326" }}>
                {formatRange(value)}
              </p>
            </motion.div>
          );
        })}
      </div>

      {/* Disclaimer */}
      <div className="flex items-start gap-2.5 px-1">
        <Info className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" style={{ color: "#C8CED0" }} />
        <p className="text-xs leading-relaxed" style={{ color: "#99A6AB" }}>
          Valores estimados com base em dados públicos de mercado. Resultados variam conforme experiência, região e contexto. Não constituem garantia de renda.
        </p>
      </div>
    </motion.div>
  );
}
